(function () {
  if (customElements.get('ce-product-subscriptions') !== undefined) {
    return;
  }

  class ProductSubscriptions extends HTMLParsedElement {
    constructor() {
      super();

      this.queuedAttributeChangedCallbackParams = [];

      this.selectors = {
        product: 'ce-product',
        input: '[data-product-subscriptions-input]',
        purchaseOptions: '[data-product-subscriptions-purchase-options]',
        inputSellingPlanGroup: '[data-product-subscriptions-input-selling-plan-group]',
        sellingPlanGroupPanel: '[data-product-subscriptions-selling-plan-group-panel]',
        inputSellingPlan: '[data-product-subscriptions-input-selling-plan]',
        sellingPlanDescriptionContainer: '[data-product-subscriptions-selling-plan-description-container]',
        sellingPlanDescription: '[data-product-subscriptions-selling-plan-description]',
        json: '[data-product-subscriptions-json]',
      };

      this.elements = null;
      this.json = null;
      this.hasLoaded = false;

      this.sectionId = this.getAttribute('section-id');

      this.handleProductVariantId = this.handleProductVariantId.bind(this);
      this.handleProductSellingPlanId = this.handleProductSellingPlanId.bind(this);
      this.handleChange = this.handleChange.bind(this);
    }

    get variantId() {
      return this.getAttribute('variant-id');
    }

    set variantId(value) {
      this.setAttribute('variant-id', value);
    }

    get sellingPlanId() {
      return this.getAttribute('selling-plan-id');
    }

    set sellingPlanId(value) {
      this.setAttribute('selling-plan-id', value);
    }

    static get observedAttributes() {
      return ['variant-id'];
    }

    attributeChangedCallback(...params) {
      if (!this.parsed) {
        this.queuedAttributeChangedCallbackParams.push(params);
        return;
      }

      const [name, oldValue, newValue] = params;

      if (oldValue === newValue) {
        return;
      }

      switch (name) {
        case 'variant-id':
          if (newValue !== null) {
            const variant = this.json.variants[newValue];

            if (!this.hasLoaded) {
              this.sellingPlanId = this.json.initial_selling_plan_id;
              this.sellingPlanGroupId = this.json.initial_selling_plan_group_id;
            } else {
              if (variant.requires_selling_plan) {
                this.sellingPlanId = variant.selling_plan_groups[0].selling_plans[0].id;
                this.sellingPlanGroupId = variant.selling_plan_groups[0].id;
              } else {
                this.sellingPlanId = '-1';
                this.sellingPlanGroupId = '-1';
              }
            }

            this._createPurchaseOptionsHtml(variant);
            this.elements.product.setSellingPlanWithId(this.sellingPlanId);
            this._onChangeSellingPlanGroupId(this.sellingPlanGroupId);

            this.hasLoaded = true;
          }

          break;
      }
    }

    parsedCallback() {
      this.elements = {
        product: this.closest(this.selectors.product),
        input: this.querySelector(this.selectors.input),
        purchaseOptions: this.querySelector(this.selectors.purchaseOptions),
        sellingPlanDescriptionContainer: this.querySelector(this.selectors.sellingPlanDescriptionContainer),
        sellingPlanDescription: this.querySelector(this.selectors.sellingPlanDescription),
        json: this.querySelector(this.selectors.json),
      };

      this.json = JSON.parse(this.elements.json.textContent);
      this.elements.json.remove();

      this.elements.product.addEventListener('ce-product:variant-id', this.handleProductVariantId);
      this.elements.product.addEventListener('ce-product:selling-plan-id', this.handleProductSellingPlanId);
      this.addEventListener('change', this.handleChange);

      for (let params of this.queuedAttributeChangedCallbackParams) {
        this.attributeChangedCallback(...params);
      }
    }

    disconnectedCallback() {
      this.elements.product.removeEventListener('ce-product:variant-id', this.handleProductVariantId);
      this.elements.product.removeEventListener('ce-product:selling-plan-id', this.handleProductSellingPlanId);
      this.removeEventListener('change', this.handleChange);
    }

    handleProductVariantId(event) {
      this.variantId = event.detail;
    }

    handleProductSellingPlanId(event) {
      this.sellingPlanId = event.detail;
      this._onChangeSellingPlanId(this.sellingPlanId);
    }

    handleChange(event) {
      if (event.target.hasAttribute('data-product-subscriptions-input-selling-plan')) {
        const id = event.target.value;
        this.elements.product.setSellingPlanWithId(id);
      }

      if (event.target.hasAttribute('data-product-subscriptions-input-selling-plan-group')) {
        this.sellingPlanGroupId = event.target.value;
        this._onChangeSellingPlanGroupId(this.sellingPlanGroupId);
      }
    }

    // RBC: Recharge group-level options aren't limited to "Recharge Plan ID"
    // + "Order Frequency and Unit" — some products have picked up an extra,
    // unlabeled numeric field (observed rendering as three radios all just
    // reading "0"). Rather than blacklist known-junk fields by name (which
    // breaks the moment Recharge adds another one), only treat an option as
    // a real, customer-facing delivery frequency if at least one of its
    // values actually looks like one ("7-day", "2-week", "1-month", etc).
    _isFrequencyGroupOption(selling_plan_group, selling_plan_group_option) {
      return selling_plan_group.selling_plans.some((selling_plan) =>
        selling_plan.options.some((selling_plan_option) =>
          selling_plan_option.position === selling_plan_group_option.position &&
          /-(day|week|month)\b/i.test(String(selling_plan_option.value))
        )
      );
    }

    _createPurchaseOptionsHtml(variant) {
      if (variant.selling_plan_groups.length === 0) {
        this.elements.purchaseOptions.innerHTML = '';
        return;
      }

      const html = `
        ${((variant.requires_selling_plan && variant.selling_plan_groups.length >= 2) || (!variant.requires_selling_plan && variant.selling_plan_groups.length >= 1)) ? `
          <h4 class="mb-2">
            ${Themu.t.products.subscriptions.purchase_options}
          </h4>
        ` : ``}
        <div>
          ${!variant.requires_selling_plan ? `
            <div class="border-b border-b-body-text/25 py-5">
              <div class="relative">
                <input
                  type="radio"
                  name="purchase_option"
                  id="${this.sectionId}__selling_plan_group[-1]"
                  value="-1"
                  ${this.sellingPlanId === '-1' ? `checked="checked"` : ``}
                  class="peer sr-only"
                  data-product-subscriptions-input-selling-plan-group="-1"
                  data-product-subscriptions-input-selling-plan="-1"
                  data-selling-plan-group="-1"
                />
                <label
                  for="${this.sectionId}__selling_plan_group[-1]"
                  class="block cursor-pointer relative pl-8 select-none text-body-text before:absolute before:left-0 before:top-0 before:bg-link-alt before:h-6 before:rounded-full before:shadow-input before:shadow-link before:w-6 after:absolute after:left-1 after:top-1 after:bg-link after:h-4 after:rounded-full after:scale-0 after:motion-safe:transition-transform after:motion-safe:duration-100 after:w-4 peer-checked:after:scale-100 peer-focus-visible:outline-2 peer-focus-visible:outline-dashed peer-focus-visible:outline-link peer-focus-visible:outline-offset-2"
                >
                  <span class="block mb-1">
                    ${Themu.t.products.subscriptions.one_time_purchase}
                  </span>
                  <span class="block">
                    ${variant.price}
                  </span>
                </label>
              </div>
            </div>
          ` : ``}
          ${variant.selling_plan_groups.map((selling_plan_group) => `
            <div class="border-b border-b-body-text/25 py-5">
              <div class="relative">
                <input
                  type="radio"
                  name="purchase_option"
                  id="${this.sectionId}__selling_plan_group[${selling_plan_group.id}]"
                  value="${selling_plan_group.id}"
                  ${this.sellingPlanGroupId === selling_plan_group.id ? `checked="checked"` : ``}
                  class="peer sr-only"
                  data-product-subscriptions-input-selling-plan-group="${selling_plan_group.id}"
                />
                <label
                  for="${this.sectionId}__selling_plan_group[${selling_plan_group.id}]"
                  class="block cursor-pointer relative pl-8 select-none text-body-text before:absolute before:left-0 before:top-0 before:bg-link-alt before:h-6 before:rounded-full before:shadow-input before:shadow-link before:w-6 after:absolute after:left-1 after:top-1 after:bg-link after:h-4 after:rounded-full after:scale-0 after:motion-safe:transition-transform after:motion-safe:duration-100 after:w-4 peer-checked:after:scale-100 peer-focus-visible:outline-2 peer-focus-visible:outline-dashed peer-focus-visible:outline-link peer-focus-visible:outline-offset-2"
                >
                  <span class="block mb-1">
                    ${selling_plan_group.offerLabel || selling_plan_group.name}
                  </span>
                  <span class="block text-sm">
                    ${selling_plan_group.price}
                  </span>
                </label>
              </div>
              <div
                class="hidden ml-8 mt-4"
                data-product-subscriptions-selling-plan-group-panel
                data-selling-plan-group-id="${selling_plan_group.id}"
              >
                ${selling_plan_group.planCount > 1 ? selling_plan_group.options.filter((selling_plan_group_option) => this._isFrequencyGroupOption(selling_plan_group, selling_plan_group_option)).map((selling_plan_group_option) => `
                  <label class="font-bold-body inline-block mb-3 text-body-text">
                    Delivery frequency
                  </label>
                  <fieldset class="-mb-3">
                    <legend class="sr-only">
                      Delivery frequency
                    </legend>
                    ${selling_plan_group.selling_plans.map((selling_plan) => `
                      ${selling_plan.options.map((selling_plan_option) => `
                        ${selling_plan_option.position === selling_plan_group_option.position ? `
                          <div class="mb-3">
                            <div class="relative">
                              <input
                                type="radio"
                                name="selling_plan_group[${selling_plan_group.id}]"
                                id="${this.sectionId}__selling_plan[${selling_plan.id}]"
                                value="${selling_plan.id}"
                                ${this.sellingPlanId === selling_plan.id ? `checked="checked"` : ``}
                                class="peer sr-only"
                                data-product-subscriptions-input-selling-plan="${selling_plan.id}"
                                data-product-subscriptions-input-selling-plan-by-group="${selling_plan_group.id}"
                                data-selling-plan-group="${ selling_plan_group.id }"
                              />
                              <label
                                for="${this.sectionId}__selling_plan[${selling_plan.id}]"
                                class="block cursor-pointer relative pl-8 select-none text-body-text text-sm before:absolute before:left-0 before:top-0 before:bg-link-alt before:h-5 before:rounded-full before:shadow-input before:shadow-link before:w-5 after:absolute after:left-1 after:top-1 after:bg-link after:h-3 after:rounded-full after:scale-0 after:motion-safe:transition-transform after:motion-safe:duration-100 after:w-3 peer-checked:after:scale-100 peer-focus-visible:outline-2 peer-focus-visible:outline-dashed peer-focus-visible:outline-link peer-focus-visible:outline-offset-2"
                              >
                                ${selling_plan_option.value}
                              </label>
                            </div>
                          </div>
                        ` : ``}
                      `).join('')}
                    `).join('')}
                  </fieldset>
                `).join('') : `
                  <input
                    type="radio"
                    name="selling_plan_group[${selling_plan_group.id}]"
                    value="${selling_plan_group.selling_plans[0] ? selling_plan_group.selling_plans[0].id : ''}"
                    checked="checked"
                    class="hidden"
                    data-product-subscriptions-input-selling-plan="${selling_plan_group.selling_plans[0] ? selling_plan_group.selling_plans[0].id : ''}"
                    data-product-subscriptions-input-selling-plan-by-group="${selling_plan_group.id}"
                    data-selling-plan-group="${selling_plan_group.id}"
                  />
                `}
              </div>
            </div>
          `).join('')}
        </div>
      `;

      this.elements.purchaseOptions.innerHTML = html;
    }

    _createDescriptionHtml() {
      if (this.sellingPlanId === '-1') {
        this.elements.sellingPlanDescription.innerHTML = '';

        requestAnimationFrame(() => {
          this.elements.sellingPlanDescriptionContainer.classList.add('hidden');
        });
        return;
      }

      const variant = this.json.variants[this.variantId];

      for (let sellingPlanGroup of variant.selling_plan_groups) {
        for (let sellingPlan of sellingPlanGroup.selling_plans) {
          if (sellingPlan.id !== this.sellingPlanId) {
            continue;
          }

          const html = `
            <p class="font-bold-body mb-2">
              ${sellingPlan.price_adjustments}
            </p>
            ${sellingPlan.description ? `
              <div class="rte mb-2">
                ${sellingPlan.description}
              </div>
            ` : ``}
          `;

          this.elements.sellingPlanDescription.innerHTML = html;

          requestAnimationFrame(() => {
            this.elements.sellingPlanDescriptionContainer.classList.remove('hidden');
          });
          return;
        }
      }
    }

    _onChangeSellingPlanId(id) {
      if (id === '-1') {
        this.elements.input.value = null;
      } else {
        this.elements.input.value = id;
      }

      const inputSellingPlans = this.querySelectorAll(this.selectors.inputSellingPlan);

      for (let inputSellingPlan of inputSellingPlans) {
        if (inputSellingPlan.value === id) {
          inputSellingPlan.checked = true;

          const sellingPlanGroupId = inputSellingPlan.dataset.sellingPlanGroup;
          const inputSellingPlanGroup = this.querySelector(`[data-product-subscriptions-input-selling-plan-group="${sellingPlanGroupId}"]`);
          inputSellingPlanGroup.checked = true;
        } else {
          inputSellingPlan.checked = false;
        }
      }

      this._createDescriptionHtml();
    }

    _onChangeSellingPlanGroupId(id) {
      const sellingPlanGroupPanels = this.querySelectorAll(this.selectors.sellingPlanGroupPanel);

      for (let sellingPlanGroupPanel of sellingPlanGroupPanels) {
        if (sellingPlanGroupPanel.dataset.sellingPlanGroupId === id) {
          sellingPlanGroupPanel.classList.remove('hidden');
        } else {
          sellingPlanGroupPanel.classList.add('hidden');
        }
      }

      if (id !== '-1') {
        const inputSellingPlans = this.querySelectorAll(`[data-product-subscriptions-input-selling-plan-by-group="${id}"]`);

        for (let inputSellingPlan of inputSellingPlans) {
          if (inputSellingPlan.value === this.sellingPlanId) {
            return;
          }
        }

        if (inputSellingPlans === null) {
          this.elements.product.setSellingPlanWithId('-1');
        } else {
          const sellingPlanId = inputSellingPlans[0].value;
          this.elements.product.setSellingPlanWithId(sellingPlanId);
        }
      }
    }
  }

  customElements.define('ce-product-subscriptions', ProductSubscriptions);
})();
